import type {
  ChangeEventHandler,
  FocusEventHandler,
  InputHTMLAttributes,
  LabelHTMLAttributes,
  ReactNode,
  Ref,
} from 'react';
import { createElement, forwardRef } from 'react';

import classNames from 'classnames';
import type { FormikContextType } from 'formik';
import { connect as formikConnect, getIn } from 'formik';
import snakeCase from 'lodash/snakeCase';
import trimStart from 'lodash/trimStart';
import InputMask from 'react-input-mask';
import type { Props as InputMaskProps } from 'react-input-mask';
import { compose, mapProps } from 'recompact';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import { Progress } from 'src/app/components/Elements/Progress/index';
import { InputContainer, RightAdornmentContainer } from 'src/app/components/Form/TextInput/styles';
import { segmentTrack } from 'src/app/state/actions';
import { useDispatch } from 'src/app/state/store';
import { CAPSULE_ICON_STRINGS, FORM_INPUT_ICON_STRINGS } from 'src/consts';
import { TEXT_INPUT_ERROR_TEXT, TEXT_INPUT_HELPER_TEXT } from 'src/consts/testIDs';

import { FormError } from '../FormError';

import styles from './styles.module.scss';

export interface TextInputUnwiredProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value'>,
    Partial<Pick<InputMaskProps, 'mask' | 'maskChar' | 'formatChars' | 'alwaysShowMask' | 'beforeMaskedValueChange'>> {
  value?: string;
  customInputClassName?: string;
  label?: string;
  error?: string;
  type?: string;
  helperText?: string;
  compactStyle?: boolean;
  labelProps?: LabelHTMLAttributes<HTMLLabelElement>;
  hasSpellCheck?: boolean;
  uppercaseLabelWhenActive?: boolean;
  forwardedRef?: Ref<HTMLInputElement | null>;

  /**
   * string name of the icon to be displayed
   */
  iconString?: FORM_INPUT_ICON_STRINGS;

  /**
   * You can adorn the input with buttons or icons. Please consider accessibility if adding buttons within the input.
   */
  adornment?: {
    /** We don't currently support left-adorned inputs, but this API is prepared for it in a non-breaking manner. */
    location: 'right';
    component: ReactNode;
  };

  /**
   * if true, applies '.optional' styles when not hovered, nor focused
   */
  optional?: boolean;

  /**
   * Boolean that indicates that a spinner should be displayed.
   */
  isLoading?: boolean;

  /**
   * Boolean that indicates that a spinner could be displayed.
   */
  hasLoader?: boolean;

  /**
   * Boolean that indicates that the text, label and underline are white.
   */
  isWhite?: boolean;

  /**
   * Boolean that indicates that the text is white.
   */
  hasWhiteTextOnly?: boolean;

  /**
   * Boolean that indicates that the error label should be the red color of Capsule's new design system.
   */
  hasLightRedError?: boolean;

  /**
   * Boolean that indicates the success style should be shown
   */
  isSuccess?: boolean;

  /**
   * Boolean that indicates the error style should be shown
   */
  isError?: boolean;

  /**
   * Boolean that indicates whether to use a medium contrast of green for a11y
   */
  mediumContrast?: boolean;

  /**
   * Boolean that indicates whether to use a higher contrast of green for a11y
   */
  highContrast?: boolean;

  /**
   * Boolean that indicates whether the label should animate when focused, defaults to true
   */
  isLabelAnimated?: boolean;
}

export const TextInputUnwired = ({
  adornment,
  id,
  name,
  label,
  type,
  autoComplete,
  error,
  value,
  onChange,
  onBlur,
  className,
  customInputClassName,
  placeholder,
  helperText,
  disabled,
  labelProps,
  mask,
  compactStyle,
  forwardedRef,
  iconString,
  optional,
  hasLoader,
  isLoading,
  isWhite,
  hasLightRedError,
  isSuccess,
  isError,
  mediumContrast,
  highContrast,
  hasWhiteTextOnly,
  isLabelAnimated = true,
  hasSpellCheck = false,
  uppercaseLabelWhenActive = false,
  ...props
}: TextInputUnwiredProps) => {
  const dispatch = useDispatch();
  const dispatchFieldFocused: FocusEventHandler<HTMLInputElement> = ({ target }) => {
    const { name } = target;
    const payload = {
      pagepath: window.location.pathname,
      fieldname: snakeCase(name),
    };
    return dispatch(segmentTrack(UNIFIED.fieldFocused, payload));
  };

  const hasValidIcon = iconString && iconString in FORM_INPUT_ICON_STRINGS;

  const TextInputClassNames = classNames(className, styles.component, {
    [styles.disabled]: disabled,
    [styles.error]: error || isError,
    [styles.hasText]: value,
    [styles.compact]: compactStyle,
    [styles.hasIcon]: hasValidIcon,
    [styles.hasRightAdornment]: adornment?.location === 'right',
    [styles.optional]: optional,
    [styles.hasLoader]: hasLoader && isLoading,
    [styles.isWhite]: isWhite,
    [styles.hasWhiteTextOnly]: hasWhiteTextOnly,
    [styles.mediumContrast]: mediumContrast,
    [styles.highContrast]: highContrast,
    [styles.hasLightRedError]: hasLightRedError,
    [styles.isSuccess]: isSuccess,
    [styles.isLabelAnimated]: isLabelAnimated,
    [styles.uppercaseLabelWhenActive]: uppercaseLabelWhenActive,
    [styles.showPlaceholder]: !!placeholder,
  });
  // null is still a permitted mask
  const tag = mask === undefined ? 'input' : InputMask;
  const errorId = `error-${name}`;

  const hasError = !!error;

  const description = hasError ? errorId : helperText ?? undefined;

  interface InputAttributes {
    type: string;
    id: string;
    name: string;
    value: string;
    onChange: ChangeEventHandler<HTMLInputElement>;
    onBlur: FocusEventHandler<HTMLInputElement>;
    onFocus: FocusEventHandler<HTMLInputElement>;
    placeholder: string;
    className: string;
    autoComplete: string;
    disabled: boolean;
    spellCheck: boolean;
    'aria-invalid': boolean;
    'aria-describedby': string;
    ref: Ref<HTMLInputElement | null>;
  }

  interface InputMaskAttributes extends InputAttributes {
    mask: string | (string | RegExp)[];
  }

  type AttributesType = typeof tag extends string ? InputAttributes : InputMaskAttributes;

  const attributes = {
    type,
    id: id || name,
    name,
    value: trimStart(value),
    onChange,
    onBlur,
    onFocus: dispatchFieldFocused,
    placeholder: placeholder || label,
    className: `${styles.input}${customInputClassName ? ` ${customInputClassName}` : ''}`,
    autoComplete,
    disabled,
    spellCheck: hasSpellCheck,
    mask,
    'aria-invalid': hasError,
    'aria-describedby': !!props['aria-describedby'] ? props['aria-describedby'].concat(` ${description}`) : description,
    ...props,
    ref: forwardedRef,
  } as AttributesType;

  return (
    <InputContainer>
      <div className={TextInputClassNames} data-cy="textInputVisible">
        {createElement(tag, attributes)}
        <div className={classNames(styles.label)}>
          <label htmlFor={id || name} {...labelProps}>
            {label}
          </label>
        </div>

        {adornment?.location === 'right' && (
          <RightAdornmentContainer hasError={hasError}>{adornment.component}</RightAdornmentContainer>
        )}

        {(error || helperText) && (
          <FormError
            errorId={errorId}
            error={error || helperText}
            className={styles.helperText}
            data_cy={error ? TEXT_INPUT_ERROR_TEXT : TEXT_INPUT_HELPER_TEXT}
          />
        )}
        {hasValidIcon && <CapsuleIcon className={styles.icon} iconString={FORM_INPUT_ICON_STRINGS[iconString]} />}
        {isLoading && (
          <div className={styles.loader}>
            <Progress color={'#0f3c6c'} size={14} thickness={2} />
          </div>
        )}
        {isSuccess && !isLoading && (
          <CapsuleIcon
            className={classNames(styles.icon, styles.successIcon)}
            iconString={CAPSULE_ICON_STRINGS.check_circle}
          />
        )}
      </div>
    </InputContainer>
  );
};

const TextInputFormikWired = compose(
  formikConnect,
  mapProps((parentProps: TextInputUnwiredProps & { formik: FormikContextType<any> }) => {
    const { name, formik, onBlur } = parentProps;
    const { values, touched, errors, handleChange, handleBlur } = formik;
    const error = getIn(touched, name!) && getIn(errors, name!);
    const value = getIn(values, name!);

    const onBlurComposite: InputHTMLAttributes<HTMLInputElement>['onBlur'] = event => {
      if (onBlur) {
        onBlur(event);
      }

      if (handleBlur) {
        handleBlur(event);
      }
    };

    return {
      ...parentProps,
      error,
      value,
      onChange: handleChange,
      onBlur: onBlurComposite,
    };
  })
)(TextInputUnwired);

/**
 * Forwarding refs to composed components
 * @see https://github.com/acdlite/recompose/issues/640#issuecomment-390185905
 */
const TextInputFormikWiredWithRef = forwardRef<HTMLInputElement, TextInputUnwiredProps>((props, ref) => (
  <TextInputFormikWired {...props} forwardedRef={ref} />
));

export default TextInputFormikWiredWithRef;
